import {
  EventType,
  FilterDimension,
  ReviewReason,
  ReviewTimelineTickFlag,
} from "src/store/api/generatedApi";
import { TARGETED_REVIEW_REASONS } from "components/manualReview/utils/reviewReasons";

export interface FilterableEvent {
  comment?: string | null;
  flags?: ReviewTimelineTickFlag[];
  material?: string | null;
  area?: string | null;
  event_type?: EventType | null;
  verified?: boolean;
  targeted_review_reasons?: ReviewReason[];
}

export const EVENT_TYPE_VALUES = ["drop", "lift", "flag"] as const;

export const HAS_COMMENT_FILTER = "has_comment" as const;
export const CONSUMPTION_EVENT_FILTER = "consumption_event" as const;
export const RAILCARS_FILTER = "railcars" as const;

export const VERIFIED_LIMIT = "verified" as const;
export const EVENT_LIMIT_VALUES = [VERIFIED_LIMIT] as const;
export type EventLimit = (typeof EVENT_LIMIT_VALUES)[number];

export const DIMENSION_MATERIAL: FilterDimension = "material";
export const DIMENSION_AREA: FilterDimension = "area";
export const DIMENSION_EVENT_TYPE: FilterDimension = "event_type";
export const FILTER_DIMENSION_VALUES = [
  DIMENSION_MATERIAL,
  DIMENSION_AREA,
  DIMENSION_EVENT_TYPE,
] as const;

export const UNKNOWN_LABEL_FILTER_VALUE = "unknown";

export interface MaterialAreaFilter {
  material: string | null;
  area: string | null;
  eventType: EventType | null;
  limitTo: EventLimit | null;
  negated?: FilterDimension[];
}

export const FLAGGED_REVIEW_REASON = "flagged" as const;
export const UNFLAGGED_REVIEW_REASON = "unflagged" as const;
export type ReviewReasonFilterValue =
  ReviewReason | typeof FLAGGED_REVIEW_REASON | typeof UNFLAGGED_REVIEW_REASON;

export interface ReviewReasonFilter {
  reason: ReviewReasonFilterValue | null;
}

export const requestedReviewReasons = (
  filters: ReviewReasonFilter[]
): ReviewReason[] => [
  ...new Set(
    filters.flatMap((filter) =>
      filter.reason === null
        ? []
        : filter.reason === FLAGGED_REVIEW_REASON ||
            filter.reason === UNFLAGGED_REVIEW_REASON
          ? TARGETED_REVIEW_REASONS
          : [filter.reason]
    )
  ),
];

export const parseEventLimit = (value: string): EventLimit | null =>
  (EVENT_LIMIT_VALUES as readonly string[]).includes(value)
    ? (value as EventLimit)
    : null;

export const EVENT_FILTER_VALUES = [
  HAS_COMMENT_FILTER,
  CONSUMPTION_EVENT_FILTER,
  RAILCARS_FILTER,
] as const;

export type ManualReviewEventFilter = (typeof EVENT_FILTER_VALUES)[number];

export const matchesEventFilter = (
  event: FilterableEvent,
  filter: ManualReviewEventFilter
): boolean => {
  switch (filter) {
    case "has_comment":
      return event.comment != null && event.comment !== "";
    case "consumption_event":
      return event.flags?.includes("consumption") ?? false;
    case "railcars":
      return (
        event.flags?.some(
          (flag) => flag === "railcar_arrival" || flag === "railcar_departure"
        ) ?? false
      );
  }
};

export const matchesAnyFilter = (
  event: FilterableEvent,
  filters: ManualReviewEventFilter[]
): boolean => {
  if (filters.length === 0) return true;
  return filters.some((filter) => matchesEventFilter(event, filter));
};

const passesLimit = (
  event: FilterableEvent,
  limitTo: EventLimit | null
): boolean => limitTo === null || event.verified === true;

const matchesDimension = <T>(
  eventValue: T | null | undefined,
  filterValue: T | null,
  negated: boolean
): boolean =>
  filterValue === null
    ? true
    : negated
      ? eventValue !== filterValue
      : eventValue === filterValue;

const matchesLabelDimension = (
  eventValue: string | null | undefined,
  filterValue: string | null,
  negated: boolean
): boolean => {
  if (filterValue === null) return true;
  if (filterValue === UNKNOWN_LABEL_FILTER_VALUE) {
    const missing = eventValue == null;
    return negated ? !missing : missing;
  }
  return negated ? eventValue !== filterValue : eventValue === filterValue;
};

export const matchesReviewReasonFilter = (
  event: FilterableEvent,
  filter: ReviewReasonFilter
): boolean =>
  filter.reason === null
    ? true
    : filter.reason === FLAGGED_REVIEW_REASON
      ? (event.targeted_review_reasons ?? []).length > 0
      : filter.reason === UNFLAGGED_REVIEW_REASON
        ? (event.targeted_review_reasons ?? []).length === 0
        : (event.targeted_review_reasons ?? []).includes(filter.reason);

export const matchesMaterialAreaFilter = (
  event: FilterableEvent,
  filter: MaterialAreaFilter
): boolean => {
  const negated = new Set(filter.negated);
  return (
    matchesLabelDimension(
      event.material,
      filter.material,
      negated.has(DIMENSION_MATERIAL)
    ) &&
    matchesLabelDimension(
      event.area,
      filter.area,
      negated.has(DIMENSION_AREA)
    ) &&
    matchesDimension(
      event.event_type,
      filter.eventType,
      negated.has(DIMENSION_EVENT_TYPE)
    ) &&
    passesLimit(event, filter.limitTo)
  );
};

export const matchesAnyEventFilter = (
  event: FilterableEvent,
  categoryFilters: ManualReviewEventFilter[],
  consumptionLimitTo: EventLimit | null,
  materialAreaFilters: MaterialAreaFilter[],
  reviewReasonFilters: ReviewReasonFilter[]
): boolean => {
  if (
    categoryFilters.length === 0 &&
    materialAreaFilters.length === 0 &&
    reviewReasonFilters.length === 0
  ) {
    return true;
  }
  const matchesCategory = categoryFilters.some(
    (filter) =>
      matchesEventFilter(event, filter) &&
      (filter !== CONSUMPTION_EVENT_FILTER ||
        passesLimit(event, consumptionLimitTo))
  );
  const matchesMaterialArea = materialAreaFilters.some((filter) =>
    matchesMaterialAreaFilter(event, filter)
  );
  const matchesReviewReason = reviewReasonFilters.some((filter) =>
    matchesReviewReasonFilter(event, filter)
  );
  return matchesCategory || matchesMaterialArea || matchesReviewReason;
};
